import { useState, useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tooltip } from '@/components/ui/tooltip'
import { Plus, X, Check, CheckCircle2, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// KeywordInput Component with Registration UI
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface KeywordInputProps {
  maxKeywords?: number
  registeredKeywords?: string[]
  onRegisteredKeywordsChange?: (keywords: string[]) => void
}

interface KeywordItem {
  value: string
  isRegistered: boolean
}

export function KeywordInput({
  maxKeywords = 20,
  registeredKeywords,
  onRegisteredKeywordsChange,
}: KeywordInputProps) {
  const [keywords, setKeywords] = useState<KeywordItem[]>([
    { value: '', isRegistered: false },
    { value: '', isRegistered: false },
    { value: '', isRegistered: false },
  ])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Sync internal state with external registeredKeywords prop
  useEffect(() => {
    if (registeredKeywords && registeredKeywords.length > 0) {
      const newKeywords: KeywordItem[] = registeredKeywords.map((keyword) => ({
        value: keyword,
        isRegistered: true,
      }))

      // Ensure minimum 3 input fields
      while (newKeywords.length < 3) {
        newKeywords.push({ value: '', isRegistered: false })
      }

      setKeywords(newKeywords)
    }
  }, [registeredKeywords])

  const handleInputChange = (index: number, value: string) => {
    const newKeywords = [...keywords]
    newKeywords[index] = { value, isRegistered: false }
    setKeywords(newKeywords)
  }

  const handleRegisterKeyword = (index: number) => {
    const keyword = keywords[index].value.trim()

    if (!keyword) {
      return
    }

    if (keyword.length < 2) {
      toast.error('키워드는 최소 2자 이상 입력해주세요.')
      return
    }

    // Check for duplicates
    const isDuplicate = keywords.some(
      (k, i) => i !== index && k.isRegistered && k.value.trim() === keyword
    )

    if (isDuplicate) {
      toast.error('이미 등록된 키워드입니다.')
      return
    }

    const newKeywords = [...keywords]
    newKeywords[index] = { value: keyword, isRegistered: true }
    setKeywords(newKeywords)

    // Notify parent component
    const registeredKeywords = newKeywords.filter((k) => k.isRegistered).map((k) => k.value.trim())

    if (onRegisteredKeywordsChange) {
      onRegisteredKeywordsChange(registeredKeywords)
    }
  }

  const handleUnregisterKeyword = (index: number) => {
    const newKeywords = [...keywords]
    newKeywords[index] = { value: newKeywords[index].value, isRegistered: false }
    setKeywords(newKeywords)

    // Notify parent component
    const registeredKeywords = newKeywords.filter((k) => k.isRegistered).map((k) => k.value.trim())

    if (onRegisteredKeywordsChange) {
      onRegisteredKeywordsChange(registeredKeywords)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // An IME composition uses Enter to commit the candidate; that keystroke is not
    // a submit. Without this guard every Korean keyword was registered mid-compose.
    if (e.nativeEvent.isComposing) return

    if (e.key === 'Enter') {
      e.preventDefault()

      const keyword = keywords[index].value.trim()

      // Only proceed if keyword is valid
      if (!keyword || keyword.length < 2) {
        return
      }

      // Check for duplicates
      const isDuplicate = keywords.some(
        (k, i) => i !== index && k.isRegistered && k.value.trim() === keyword
      )

      if (isDuplicate) {
        return
      }

      // Register the keyword
      handleRegisterKeyword(index)

      // After registration, move focus to next input field
      setTimeout(() => {
        // Calculate next index
        const nextIndex = index + 1

        // Check if we need to create a new field
        const needsNewField = nextIndex >= keywords.length

        if (needsNewField) {
          // Same call the button makes, so Enter at the cap says why instead of
          // quietly refusing to move the cursor.
          if (addKeywordInput()) {
            // Focus will be set after state update
            setTimeout(() => {
              inputRefs.current[nextIndex]?.focus()
            }, 0)
          }
        } else {
          // Focus existing next input
          inputRefs.current[nextIndex]?.focus()
        }
      }, 0)
    }
  }

  /**
   * Append an empty field, or explain why not.
   *
   * @returns whether a field was added — the Enter handler needs to know before it
   * tries to focus one that does not exist.
   *
   * The button stays rendered and enabled at the cap. It used to disappear entirely,
   * which is the same failure as disabling it: the control vanishes and nothing says
   * the account has a limit, so the screen reads as broken rather than full. A sibling
   * repo settled this the same way (nangtul #33 — keep the control, explain in a
   * toast). No price and no "upgrade": paid plans may never exist (P3-14), and copy
   * that survives that answer is the only copy worth writing.
   */
  const addKeywordInput = (): boolean => {
    if (keywords.length >= maxKeywords) {
      toast.warning(
        '키워드 한도',
        `지금 계정은 키워드를 ${maxKeywords}개까지 등록할 수 있어요. 쓰지 않는 키워드를 지우면 자리가 생겨요.`
      )
      return false
    }
    setKeywords([...keywords, { value: '', isRegistered: false }])
    return true
  }

  const registeredCount = keywords.filter((k) => k.isRegistered).length

  const handleClearAll = () => {
    // Reset to initial 3 empty fields
    setKeywords([
      { value: '', isRegistered: false },
      { value: '', isRegistered: false },
      { value: '', isRegistered: false },
    ])

    // Notify parent component
    if (onRegisteredKeywordsChange) {
      onRegisteredKeywordsChange([])
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-base font-semibold">검색 키워드</Label>
          <Tooltip
            content={
              <div className="space-y-1">
                <p className="mb-1 font-semibold">💡 사용 방법:</p>
                <ul className="ml-4 list-disc space-y-0.5">
                  <li>키워드를 입력하고 체크 버튼을 클릭하세요</li>
                  <li>Enter 키를 눌러도 등록됩니다</li>
                  <li>등록된 키워드는 파란색으로 표시됩니다</li>
                  <li>최소 1개 이상의 키워드를 등록해야 검색할 수 있습니다</li>
                </ul>
              </div>
            }
          >
            <Info className="h-4 w-4 cursor-help text-muted-foreground hover:text-primary" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary">
            {registeredCount}/{maxKeywords}
          </span>
          {registeredCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-7 px-2 text-xs"
            >
              전체 초기화
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {keywords.map((keyword, index) => (
          <div key={index} className="space-y-1.5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  value={keyword.value}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  placeholder={`키워드 ${index + 1}`}
                  disabled={keyword.isRegistered}
                  className={cn(
                    'pr-10',
                    keyword.isRegistered && 'border-primary bg-primary/5 font-medium text-primary'
                  )}
                />
                {keyword.isRegistered && (
                  <CheckCircle2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-primary" />
                )}
              </div>

              <Button
                type="button"
                variant={keyword.isRegistered ? 'outline' : 'default'}
                size="icon"
                onClick={() =>
                  keyword.isRegistered
                    ? handleUnregisterKeyword(index)
                    : handleRegisterKeyword(index)
                }
                disabled={!keyword.isRegistered && !keyword.value.trim()}
                className="shrink-0"
                aria-label={keyword.isRegistered ? '등록 해제' : '등록'}
              >
                {keyword.isRegistered ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Always rendered, always clickable — see `addKeywordInput`. */}
      <Button type="button" variant="outline" onClick={addKeywordInput} className="w-full">
        <Plus className="mr-2 h-4 w-4" />
        키워드 입력 필드 추가
      </Button>
    </div>
  )
}
